/**
 * Error Handler - Normalize errors from different sources
 *
 * Features:
 * - Format errors from Supabase: { data, error }
 * - Format errors from Backend Custom: { success, data, error }
 * - Format network errors (fetch failures)
 * - Unified output: { has_error, error_type, error_message }
 *
 * @module utils/errorHandler
 *
 * @example
 * import { parseError, formatErrorMessage } from '@/utils/errorHandler';
 *
 * // Parse Supabase error
 * const { data, error } = await supabase.from('artworks').select();
 * const error_state = parseError(error, 'SUPABASE');
 *
 * // Parse Backend Custom error
 * const { success, error } = await apiClient.get('/api/orders');
 * const error_state = parseError(error, 'BACKEND_CUSTOM');
 *
 * // Display error message
 * if (error_state.has_error) {
 *   const user_message = formatErrorMessage(error_state);
 *   alert(user_message);
 * }
 */

/**
 * Error types recognized by the error handler
 * @enum {string}
 */
export const ERROR_TYPES = {
  // Supabase errors
  NOT_FOUND: 'NOT_FOUND',
  FETCH_ERROR: 'FETCH_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',

  // Backend Custom errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SERVER_ERROR: 'SERVER_ERROR',

  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',

  // Generic
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
};

/**
 * Parse error from different sources and normalize to unified format
 *
 * @param {any} error - Error object from various sources
 * @param {string} [source='AUTO'] - Error source: 'SUPABASE', 'BACKEND_CUSTOM', 'NETWORK', 'AUTO' (auto-detect)
 * @returns {{has_error: boolean, error_type: string|null, error_message: string|null, original_error?: any}}
 *
 * @example
 * // Auto-detect source
 * const error_state = parseError(error);
 *
 * // Explicit Supabase error
 * const error_state = parseError(error, 'SUPABASE');
 *
 * // Explicit Backend Custom error
 * const error_state = parseError(error, 'BACKEND_CUSTOM');
 */
export function parseError(error, source = 'AUTO') {
  // No error case
  if (!error) {
    return {
      has_error: false,
      error_type: null,
      error_message: null,
    };
  }

  // Auto-detect source if not specified
  if (source === 'AUTO') {
    source = detectErrorSource(error);
  }

  // Parse based on source
  switch (source) {
    case 'SUPABASE':
      return parseSupabaseError(error);

    case 'BACKEND_CUSTOM':
      return parseBackendCustomError(error);

    case 'NETWORK':
      return parseNetworkError(error);

    default:
      return parseUnexpectedError(error);
  }
}

/**
 * Detect error source automatically
 * @private
 * @param {any} error - Error object
 * @returns {string} Detected source: 'SUPABASE', 'BACKEND_CUSTOM', 'NETWORK', 'UNKNOWN'
 */
function detectErrorSource(error) {
  // Supabase error format: { code, message, details, hint }
  if (error && typeof error === 'object' && ('code' in error || 'details' in error)) {
    return 'SUPABASE';
  }

  // Backend Custom error format: string or { error: string }
  if (typeof error === 'string') {
    return 'BACKEND_CUSTOM';
  }

  // Network error: Error instance
  if (error instanceof Error) {
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      return 'NETWORK';
    }
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return 'NETWORK';
    }
    // JSON parse error (server returned HTML instead of JSON — backend unreachable or wrong URL)
    if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
      return 'NETWORK';
    }
  }

  return 'UNKNOWN';
}

/**
 * Parse Supabase error
 * @private
 * @param {Object} error - Supabase error object
 * @returns {{has_error: boolean, error_type: string, error_message: string, original_error: any}}
 */
function parseSupabaseError(error) {
  if (!error) {
    return { has_error: false, error_type: null, error_message: null };
  }

  // Extract error details
  const error_code = error.code || 'UNKNOWN';
  const error_message = error.message || 'Database error occurred';
  const error_details = error.details || null;

  // Determine error type based on code
  let error_type = ERROR_TYPES.DATABASE_ERROR;

  if (error_code === 'PGRST116') {
    // No rows returned (not found)
    error_type = ERROR_TYPES.NOT_FOUND;
  } else if (error_code.startsWith('23')) {
    // PostgreSQL constraint violations (23xxx)
    error_type = ERROR_TYPES.VALIDATION_ERROR;
  } else if (error_code.startsWith('42')) {
    // PostgreSQL syntax/permission errors (42xxx)
    error_type = ERROR_TYPES.FORBIDDEN;
  }

  return {
    has_error: true,
    error_type,
    error_message: error_details || error_message,
    original_error: error,
  };
}

/**
 * Parse Backend Custom API error
 * @private
 * @param {string|Object} error - Backend Custom error (string or object)
 * @returns {{has_error: boolean, error_type: string, error_message: string, original_error: any}}
 */
function parseBackendCustomError(error) {
  if (!error) {
    return { has_error: false, error_type: null, error_message: null };
  }

  const error_message = typeof error === 'string' ? error : error.error || 'Request failed';

  // Detect error type from message content
  let error_type = ERROR_TYPES.SERVER_ERROR;

  const message_lower = error_message.toLowerCase();

  if (message_lower.includes('not found')) {
    error_type = ERROR_TYPES.NOT_FOUND;
  } else if (message_lower.includes('unauthorized') || message_lower.includes('authentication')) {
    error_type = ERROR_TYPES.UNAUTHORIZED;
  } else if (message_lower.includes('forbidden') || message_lower.includes('permission')) {
    error_type = ERROR_TYPES.FORBIDDEN;
  } else if (message_lower.includes('validation') || message_lower.includes('invalid')) {
    error_type = ERROR_TYPES.VALIDATION_ERROR;
  } else if (message_lower.includes('timeout')) {
    error_type = ERROR_TYPES.TIMEOUT_ERROR;
  } else if (message_lower.includes('network')) {
    error_type = ERROR_TYPES.NETWORK_ERROR;
  }

  return {
    has_error: true,
    error_type,
    error_message,
    original_error: error,
  };
}

/**
 * Parse network error (fetch failures, timeouts)
 * @private
 * @param {Error} error - Network error object
 * @returns {{has_error: boolean, error_type: string, error_message: string, original_error: any}}
 */
function parseNetworkError(error) {
  if (!error) {
    return { has_error: false, error_type: null, error_message: null };
  }

  let error_type = ERROR_TYPES.NETWORK_ERROR;
  let error_message = 'Network error occurred';

  if (error.name === 'AbortError' || error.message?.includes('timeout')) {
    error_type = ERROR_TYPES.TIMEOUT_ERROR;
    error_message = 'Request timeout - please try again';
  } else if (error.message?.includes('fetch')) {
    error_message = 'Network error - check your connection';
  }

  return {
    has_error: true,
    error_type,
    error_message,
    original_error: error,
  };
}

/**
 * Parse unexpected error (fallback)
 * @private
 * @param {any} error - Unknown error
 * @returns {{has_error: boolean, error_type: string, error_message: string, original_error: any}}
 */
function parseUnexpectedError(error) {
  return {
    has_error: true,
    error_type: ERROR_TYPES.UNEXPECTED_ERROR,
    error_message: error?.message || 'An unexpected error occurred',
    original_error: error,
  };
}

/**
 * Format error message for display to user
 *
 * @param {{has_error: boolean, error_type: string, error_message: string}} error_state - Error state from parseError()
 * @returns {string} User-friendly error message in Spanish
 *
 * @example
 * const error_state = parseError(error);
 * const message = formatErrorMessage(error_state);
 * // Returns: "No se pudo cargar los datos. Por favor, intenta nuevamente."
 */
export function formatErrorMessage(error_state) {
  if (!error_state || !error_state.has_error) {
    return '';
  }

  const { error_type, error_message } = error_state;

  // Map error types to user-friendly Spanish messages
  const user_messages = {
    [ERROR_TYPES.NOT_FOUND]: 'No se encontró el recurso solicitado.',
    [ERROR_TYPES.FETCH_ERROR]: 'No se pudo cargar los datos. Por favor, intenta nuevamente.',
    [ERROR_TYPES.DATABASE_ERROR]: 'Error de base de datos. Por favor, contacta soporte.',
    [ERROR_TYPES.VALIDATION_ERROR]: 'Datos inválidos. Por favor, verifica la información.',
    [ERROR_TYPES.UNAUTHORIZED]: 'No autorizado. Por favor, inicia sesión nuevamente.',
    [ERROR_TYPES.FORBIDDEN]: 'No tienes permisos para realizar esta acción.',
    [ERROR_TYPES.SERVER_ERROR]: 'Error del servidor. Por favor, intenta más tarde.',
    [ERROR_TYPES.NETWORK_ERROR]: 'Error de conexión. Verifica tu internet.',
    [ERROR_TYPES.TIMEOUT_ERROR]: 'La solicitud tardó demasiado. Por favor, intenta nuevamente.',
    [ERROR_TYPES.UNEXPECTED_ERROR]: 'Ocurrió un error inesperado. Por favor, intenta más tarde.',
  };

  // If the error_message is already a user-friendly Spanish string (set by a hook/component),
  // preserve it instead of replacing with a generic mapped message.
  // Detection: Spanish text contains accented chars or starts with common Spanish patterns.
  const is_already_spanish = error_message && /[áéíóúñ¿¡]/.test(error_message);
  if (is_already_spanish) {
    return error_message;
  }

  // Return mapped message or original error message
  return user_messages[error_type] || error_message || user_messages[ERROR_TYPES.UNEXPECTED_ERROR];
}

/**
 * Check if error state has error
 *
 * @param {{has_error: boolean}} error_state - Error state object
 * @returns {boolean} True if has error
 *
 * @example
 * if (hasError(error_state)) {
 *   console.error(formatErrorMessage(error_state));
 * }
 */
export function hasError(error_state) {
  return Boolean(error_state?.has_error);
}

/**
 * Create initial error state (no error)
 *
 * @returns {{has_error: boolean, error_type: null, error_message: null}}
 *
 * @example
 * const [error_state, setErrorState] = useState(createInitialErrorState());
 */
export function createInitialErrorState() {
  return {
    has_error: false,
    error_type: null,
    error_message: null,
  };
}
